import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ACCENT } from "../Auth/authStyles";
import { ProfileStep, AccountStep } from "./AmbassadorSteps";
import { SERVERURL } from "../../ServerUrl";

// CTAs across the site say who they are for, so they pass ?role= and the
// picker starts on that answer.
const ROLES = ["investor", "founder", "ambassador"];

// The heading has to echo the CTA that got you here. Landing on "Sign up"
// after clicking "Apply as a Startup" reads like the wrong page.
const COPY = {
  founder: {
    heading: "Apply as a startup",
    subcopy: "Tell us about you and your project.",
  },
  investor: {
    heading: "Join as an investor",
    subcopy: "Tell us a little bit about yourself.",
  },
  ambassador: {
    heading: "Join our mentors",
    subcopy: "Tell us a little bit about yourself.",
  },
  default: {
    heading: "Join the community",
    subcopy: "Tell us a little bit about yourself.",
  },
};

const LOGIN_PROMPT = "Already have an account?";

// The API returns terse strings. This one means "the email is registered and
// the password you typed isn't its password" — which needs a way forward,
// because the form gives you no hint that an existing account can add a role
// by re-entering its own password.
const FRIENDLY_ERRORS = {
  "Existing Account, Wrong Password!":
    "That email already has an account. Enter that account's password to add this role to it.",
};

const friendlyError = (message) => FRIENDLY_ERRORS[message] || message;

const AmbassadorForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetRole = searchParams.get("role");
  const copy = ROLES.includes(presetRole) ? COPY[presetRole] : COPY.default;
  // Same flag the navbar uses to swap Login for Logout.
  const loggedIn = Boolean(localStorage.getItem("loggedin"));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Request results render inside the card instead of a popup.
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [imgPreview, setImgPreview] = useState(null);
  const [continue1, setContinue] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [user, setUser] = useState({
    fullName: "",
    title: "",
    location: "",
    twitter: "",
    linkedIn: "",
    email: "",
    password: "",
    confirmPassword: "",
    image: "",
    phone: "",
    category: ROLES.includes(presetRole) ? presetRole : "",
  });

  const handleChange = (e) => {
    const { value, name } = e.target;
    setUser({ ...user, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear error for the field
  };

  const handleImageChange = (e) => {
    const selected = e.target.files[0];

    const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
    if (selected && ALLOWED_TYPES.includes(selected.type)) {
      setUser({ ...user, image: selected });

      let reader = new FileReader();
      reader.onloadend = () => {
        setImgPreview(reader.result);
      };
      reader.readAsDataURL(selected);
    } else {
      setErrors({
        ...errors,
        image: "Invalid image type. Only PNG, JPEG, and JPG are allowed.",
      });
    }
  };

  const continueClicked = () => {
    let newErrors = {};
    if (!user.fullName) newErrors.fullName = "Full Name is required";
    if (!user.title) newErrors.title = "Title is required";
    if (!user.location) newErrors.location = "Location is required";
    if (!user.twitter) newErrors.twitter = "Twitter is required";
    if (!user.linkedIn) newErrors.linkedIn = "LinkedIn is required";
    if (!user.image) newErrors.image = "Profile image is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setContinue(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10,15}$/; // Assuming a simple numeric check for 10 to 15 digits
    return phoneRegex.test(phone);
  };

  // `.` rather than a `[A-Za-z\d]` class: the old pattern silently rejected
  // any password containing a symbol, which is the opposite of what the error
  // message tells you to do.
  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  const signupClick = async () => {
    let newErrors = {};
    if (!user.email) newErrors.email = "Email is required";
    if (!validateEmail(user.email)) newErrors.email = "Invalid email format";
    if (!user.phone) newErrors.phone = "Phone number is required";
    if (!validatePhone(user.phone))
      newErrors.phone = "Invalid phone number format";
    if (!user.password) newErrors.password = "Password is required";
    if (!validatePassword(user.password))
      newErrors.password =
        "Password must be at least 8 characters long and contain at least one letter and one number";
    if (user.password !== user.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (!user.category) newErrors.category = "Category is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const form = new FormData();
      form.append("fullName", user.fullName);
      form.append("title", user.title);
      form.append("twitter", user.twitter);
      form.append("linkedIn", user.linkedIn);
      form.append("email", user.email);
      form.append("password", user.password);
      form.append("location", user.location);
      form.append("image", user.image);
      form.append("phone", user.phone);
      form.append("category", user.category);

      setSubmitting(true);

      try {
        const response = await axios.post(
          `${SERVERURL}/api/v1/registerSpecial`,
          form
        );

        if (response.status === 200) {
          // Substring, not equality: the approval message the API actually
          // sends has no space after "Admin.", so an exact compare silently
          // swallowed it and the form looked like it had done nothing.
          if (response.data.message?.includes("Code Sent")) {
            navigate(`/user/verify?email=${encodeURIComponent(user.email)}`);
          } else {
            // Nothing follows this one, so the confirmation stays on the card
            // instead of flashing past on the way home.
            setFormSuccess(response.data.message);
          }
        } else if (response.status === 201) {
          setFormError(friendlyError(response.data.message));
        } else if (response.status === 500) {
          setFormError("Something went wrong");
        }
      } catch (error) {
        setFormError(
          friendlyError(error.response?.data?.message) ||
            "An error occurred while registering. Please try again later."
        );
        console.error("Error registering ambassador:", error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const radioHandle = (category) => {
    setUser({
      ...user,
      category: category,
    });
    setErrors({ ...errors, category: "" }); // Clear error for category
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    if (continue1) {
      continueClicked();
    } else {
      signupClick();
    }
  };

  return (
    <div className="w-full bg-white relative overflow-hidden">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-10 pt-4 pb-10 md:pt-5 md:pb-16">
        <div className="rounded-3xl border border-black/10 bg-white p-3 md:p-4 shadow-[0_40px_80px_-32px_rgba(27,10,68,0.22)]">
          <div className="flex flex-col !flex-nowrap !justify-center px-4 py-8 md:px-10 md:py-10">
            <h1 className="font-headline font-bold tracking-tight text-[#111113] text-[clamp(1.7rem,3vw,2.4rem)] leading-[1.12] m-0">
              {copy.heading}
            </h1>

            <p className="font-body text-sm text-[#4a4753] mt-3 mb-0">
              {copy.subcopy}
            </p>

            {/* Pointless for someone who is already signed in. */}
            {!loggedIn && (
              <p className="font-body text-sm text-[#4a4753] mt-1.5 mb-0">
                {LOGIN_PROMPT}{" "}
                <Link
                  to="/user/login"
                  style={{ color: ACCENT }}
                  className="underline underline-offset-4 hover:opacity-70 transition-opacity"
                >
                  Log in
                </Link>
              </p>
            )}

            <form
              onSubmit={handleSubmit}
              className="flex flex-col !flex-nowrap gap-3 mt-8"
            >
              {continue1 ? (
                <ProfileStep
                  user={user}
                  errors={errors}
                  imgPreview={imgPreview}
                  onImageChange={handleImageChange}
                  onChange={handleChange}
                />
              ) : (
                <AccountStep
                  user={user}
                  errors={errors}
                  formError={formError}
                  formSuccess={formSuccess}
                  submitting={submitting}
                  onChange={handleChange}
                  onSelectCategory={radioHandle}
                  onBack={() => setContinue(true)}
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword(!showPassword)}
                  showConfirm={showConfirm}
                  onToggleConfirm={() => setShowConfirm(!showConfirm)}
                />
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorForm;
